import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaPlus, FaEdit } from 'react-icons/fa';
import { RiMailSendFill } from 'react-icons/ri';
import LabelCopy from './LabelCopy';
import { IoIosRefresh } from 'react-icons/io';
import ThreeColumnLayout from '../../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../../organisms/FilterSidebar';
import ContactModalForm from './ContactModalForm';
import Loading from '../../../ions/Loading';
import TextEdit from '../../../molecules/TextEdit';
import ATable from '../../../organisms/ATable';
import TableSkeleton from '../../../atoms/TableSkeleton';
import PageFooter from '../../../molecules/PageFooter';
import appConfig from '../../../../config/app-config';
import useFetch from '../../../../hooks/useFetch';

type Contact = {
  id: string;
  fullName: string;
  taxId: string;
  primaryPhone: string;
  primaryWhatsApp: string;
  primaryEmail: string;
  status: string;
  statusFormatted: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
};

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const API_KEY = COMMUNICATION_ENV.key;
const BASE_URL = COMMUNICATION_ENV.address;

export const ContactPageList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [showForm, setShowForm] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [operationInput, setOperationInput] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedOperation, setSelectedOperation] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (location.state?.refresh) {
      setRefreshKey(k => k + 1);
      globalThis.history.replaceState({}, '');
    }
  }, [location.state]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [localContacts, setLocalContacts] = useState<Contact[]>([]);

  useEffect(() => {
    setLocalContacts([]);
  }, [searchValue, selectedStatus, selectedTag, selectedType, selectedOperation, page, pageSize]);

  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    search: searchValue,
    status: selectedStatus,
    tag: selectedTag,
    operation: selectedOperation,
    sortField: sortField,
    sortOrder: sortOrder,
    contactType: selectedType,
    _r: String(refreshKey),
  });
  const url = `${BASE_URL}/contacts/page-list?${params.toString()}`;
  const fetchHeaders = useMemo(() => [{ key: 'x-api-key', value: API_KEY || '' }], []);
  const { isLoading, data } = useFetch(url, fetchHeaders);

  const contacts = (data?.list || []).map((contact: any) => ({
    ...contact,
    taxId: contact.taxIdFormatted || contact.taxId,
    contactTypeFormatted: contact.contactTypeFormatted
  }));
  const displayContacts = localContacts.length > 0
    ? [...localContacts, ...contacts.filter((c: Contact) => !localContacts.some((lc: Contact) => lc.id === c.id))]
    : contacts;
  const totalElements = data?.totalElements || 0;
  const totalFiltered = data?.totalFiltered ?? totalElements;
  const totalPages = data?.totalPages || 0;
  const totalIndividual = data?.totalIndividual || 0;
  const totalLegalPerson = data?.totalLegalPerson || 0;
  const totalByStatus = data?.totalByStatus || {};
  const totalByTag = data?.totalByTag || [];
  const statusTranslations = data?.statusTranslations || {};
  const apiInfo = {
    name: data?.apiName || 'Communication API',
    version: data?.apiVersion || '1.0.0',
    environment: data?.environment || 'development'
  };

  const getStatusDotColor = (status: string) => {
    switch (status) {
      case 'FAST': return 'bg-green-500';
      case 'WAITING': return 'bg-purple-500';
      case 'LONG_TIME': return 'bg-orange-500';
      case 'MODERATELY_FAST': return 'bg-yellow-500';
      case 'NO_RESPOND': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'FAST': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'WAITING': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'LONG_TIME': return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      case 'MODERATELY_FAST': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'NO_RESPOND': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      default: return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
    }
  };

  const statusOptions = Object.entries(totalByStatus).map(([status, count]) => ({
    label: statusTranslations[status] || status,
    value: status,
    count: count as number
  }));

  const filterGroups = [
    {
      title: '',
      children: (
        <div className="space-y-4">
          <button
            onClick={() => {
              setSelectedStatus('');
              setSelectedTag('');
              setSelectedType('');
            }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              selectedStatus === '' && selectedTag === '' && selectedType === ''
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <span>Todos</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{totalElements}</span>
            </div>
          </button>

          <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">TIPO</div>
            <div className="space-y-1">
              <button
                onClick={() => {
                  setSelectedType('INDIVIDUAL');
                  setSelectedStatus('');
                  setSelectedTag('');
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedType === 'INDIVIDUAL'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>Pessoa Física</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{totalIndividual}</span>
                </div>
              </button>
              <button
                onClick={() => {
                  setSelectedType('LEGAL_PERSON');
                  setSelectedStatus('');
                  setSelectedTag('');
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedType === 'LEGAL_PERSON'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>Pessoa Jurídica</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{totalLegalPerson}</span>
                </div>
              </button>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">FAROL</div>
            <div className="space-y-1">
              {statusOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">Carregando...</div>
              ) : (
                statusOptions.map((item) => (
                  <button
                    key={item.value}
                    onClick={() => {
                      setSelectedStatus(item.value);
                      setSelectedTag('');
                      setSelectedType('');
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedStatus === item.value
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${getStatusDotColor(item.value)}`}></span>
                        <span>{item.label}</span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{item.count}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'MARCADORES',
      children: (
        <>
          {totalByTag.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">Nenhum marcador</div>
          ) : (
            totalByTag.map((tag: any) => (
              <button
                key={tag.name}
                onClick={() => {
                  setSelectedTag(tag.name);
                  setSelectedStatus('');
                  setSelectedType('');
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedTag === tag.name
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: tag.color || '#808080' }} />
                    <span>{tag.name}</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{tag.count}</span>
                </div>
              </button>
            ))
          )}
        </>
      )
    }
  ];


  const headers = [
    { id: 'type', title: '', canSort: false, width: '40px', cssClass: 'border-b text-center' },
    { id: 'taxId', title: 'CPF/CNPJ', field: 'taxId', canSort: true, width: '140px', cssClass: 'border-b' },
    { id: 'fullName', title: 'Nome', field: 'fullName', canSort: true, cssClass: 'border-b' },
    { id: 'primaryPhone', title: 'Telefone', canSort: false, width: '150px', cssClass: 'border-b' },
    { id: 'primaryWhatsApp', title: 'WhatsApp', canSort: false, width: '150px', cssClass: 'border-b' },
    { id: 'primaryEmail', title: 'Email', canSort: false, width: '180px', cssClass: 'border-b' },
    { id: 'status', title: 'Farol', canSort: false, width: '150px', cssClass: 'border-b text-center' },
    { id: 'actions', title: '', canSort: false, width: '40px', cssClass: 'border-b text-center' },
  ];

  return (
    <>
      {isLoading && <Loading />}

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
          title="Novo Contato"
        >
          <FaPlus size={14} />
        </button>
      )}

      <ThreeColumnLayout
        leftSidebarTitle={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <RiMailSendFill className="text-blue-600" size={18} />
              <span>{apiInfo.name}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <div>v{apiInfo.version}</div>
              <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                {(apiInfo.environment || 'dev').toUpperCase()}
              </div>
            </div>
          </div>
        }
        leftSidebar={
          <FilterSidebar
            filterGroups={filterGroups}
          />
        }
        showRightPanel={showForm}
        onCloseRightPanel={() => setShowForm(false)}
        rightPanel={
          showForm ? (
            <div className="h-full p-6">
              <ContactModalForm 
                onClose={() => setShowForm(false)} 
                onSuccess={(newContact: any) => {
                  setShowForm(false);
                  const formattedContact: Contact = {
                    id: newContact.id,
                    fullName: newContact.fullName,
                    taxId: newContact.taxIdFormatted || newContact.taxId,
                    primaryPhone: newContact.primaryPhone || '',
                    primaryWhatsApp: newContact.primaryWhatsApp || '',
                    primaryEmail: newContact.primaryEmail || '',
                    status: newContact.status,
                    statusFormatted: newContact.statusFormatted,
                    notes: newContact.notes || '',
                    createdAt: newContact.createdAt,
                    updatedAt: newContact.updatedAt,
                    contactTypeFormatted: newContact.contactTypeFormatted
                  } as any;
                  setLocalContacts([formattedContact]);
                }} 
              />
            </div>
          ) : null
        }
      >
        <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
          <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Contatos</h1>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setRefreshKey(prev => prev + 1)}
                  className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title="Atualizar lista"
                >
                  <IoIosRefresh size={18} />
                </button>

                <div className="w-32">
                  <TextEdit
                    label="Operação"
                    value={operationInput}
                    onChange={(e) => setOperationInput(e.target.value.slice(0, 15))}
                    onKeyPress={(e) => { if (e.key === 'Enter') { setSelectedOperation(operationInput); setPage(1); } }}
                  />
                </div>

                <div className="w-80">
                  <TextEdit
                    label="Buscar contato"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value.slice(0, 150))}
                    onKeyPress={(e) => { if (e.key === 'Enter') { setSearchValue(searchInput); setPage(1); } }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
            {isLoading ? (
              <TableSkeleton />
            ) : (
              <ATable headers={headers} fieldSorted={sortField} orderSorted={sortOrder} onSort={(field, order) => { setSortField(field); setSortOrder(order); setPage(1); }}>
                {displayContacts.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-gray-500 dark:text-gray-400">
                      Nenhum contato encontrado
                    </td>
                  </tr>
                ) : (
                  displayContacts.map((contact: Contact, index: number) => (
                    <tr
                      key={contact.id}
                      className={`group ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'} hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer`}
                      onClick={() => navigate(`/communication/contact/${contact.id}/page-view`)}
                    >
                      <td className="py-2 px-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${(contact as any).contactTypeFormatted === 'PF' ? 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200' : 'bg-slate-700 text-white dark:bg-slate-600'}`}>
                          {(contact as any).contactTypeFormatted || '-'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-300">
                        <LabelCopy text={contact.taxId} className="text-sm text-gray-700 dark:text-gray-300" />
                      </td>
                      <td className="py-2 px-3 text-sm font-medium text-gray-900 dark:text-white truncate" title={contact.fullName}>{contact.fullName}</td>
                      <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-300">
                        {contact.primaryPhone && contact.primaryPhone !== 'Não informado' && (
                          <LabelCopy text={contact.primaryPhone} className="text-sm text-gray-700 dark:text-gray-300" />
                        )}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-300">
                        {contact.primaryWhatsApp && contact.primaryWhatsApp !== 'Não informado' && (
                          <LabelCopy text={contact.primaryWhatsApp} className="text-sm text-gray-700 dark:text-gray-300" />
                        )}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-300 truncate max-w-[250px]" title={contact.primaryEmail}>
                        {contact.primaryEmail && contact.primaryEmail !== 'Não informado' && (
                          <LabelCopy text={contact.primaryEmail} className="text-sm text-gray-700 dark:text-gray-300" />
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <div className="flex justify-center">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(contact.status)}`}>
                            {contact.statusFormatted}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/communication/contact/${contact.id}/page-view`);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-opacity"
                        >
                          <FaEdit size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </ATable>
            )}
          </div>

          <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <PageFooter
              currentPage={page - 1}
              totalPages={totalPages}
              itemsPerPage={pageSize}
              totalElements={totalFiltered}
              onPageChange={(p) => setPage(p + 1)}
              onItemsPerPageChange={(size) => { setPageSize(size); setPage(1); }}
              itemsLabel="contatos"
            />
          </div>
        </div>
      </ThreeColumnLayout>
    </>
  );
};
